import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  newClauseId,
  normalizeClauseRows,
  normalizeSpecialRows,
} from "@/pages/admin/ContractClauseEditor";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPageHeader,
  AdminPagination,
  AdminSubTabs,
  paginateItems,
  previewText,
  useAdminQueryParam,
} from "@/pages/admin/AdminUx";

const SUBS = [
  { id: "clauses", label: "기본 조항" },
  { id: "specials", label: "기본 특약" },
];

export default function ContractClausesTab() {
  const [clauses, setClauses] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [section, setSection] = useAdminQueryParam("section", "clauses");
  const sub = section === "specials" ? "specials" : "clauses";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contract-clauses");
      setClauses(normalizeClauseRows(r.data.clauses || []));
      setSpecials(normalizeSpecialRows(r.data.specials || []));
    } catch {
      toast.error("계약 조항 목록 로드 실패");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [sub, q]);

  const persist = async (nextClauses, nextSpecials) => {
    setSaving(true);
    try {
      await api.put("/admin/contract-clauses", {
        clauses: normalizeClauseRows(nextClauses),
        specials: normalizeSpecialRows(nextSpecials),
      });
      toast.success("기본 계약 조항이 저장되었습니다. 이미 생성된 계약에는 적용되지 않습니다.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
    setSaving(false);
  };

  const restore = async () => {
    if (!window.confirm("기본 조항을 현재 정책 시드로 복원하시겠습니까? 이미 생성된 계약은 변경되지 않습니다.")) return;
    setSaving(true);
    try {
      await api.post("/admin/contract-clauses/restore");
      toast.success("기본 조항을 복원했습니다.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "복원 실패");
    }
    setSaving(false);
  };

  const filteredClauses = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clauses;
    return clauses.filter((row) => {
      const hay = [`제${row.article_no}조`, row.article_no, row.title, row.content].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [clauses, q]);

  const filteredSpecials = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return specials;
    return specials.filter((row) => String(row.text || "").toLowerCase().includes(needle));
  }, [specials, q]);

  const source = sub === "specials" ? filteredSpecials : filteredClauses;
  const pageSize = sub === "specials" ? 8 : 8;
  const paged = paginateItems(source, page, pageSize);
  useEffect(() => { if (page !== paged.page) setPage(paged.page); }, [page, paged.page]);

  const move = (list, setList, id, dir) => {
    const i = list.findIndex((row) => row.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };

  const addClause = () => {
    const maxNo = clauses.reduce((n, row) => Math.max(n, Number(row.article_no) || 0), 0);
    const row = { id: newClauseId(), article_no: maxNo + 1, title: "", content: "", enabled: true };
    setClauses(normalizeClauseRows([...clauses, row]));
    setEditing({ kind: "clause", ...row });
  };

  const addSpecial = () => {
    const row = { id: newClauseId(), text: "", enabled: true };
    setSpecials(normalizeSpecialRows([...specials, row]));
    setEditing({ kind: "special", ...row });
  };

  const saveDrawer = () => {
    if (!editing) return;
    if (editing.kind === "special") {
      setSpecials((prev) => normalizeSpecialRows(prev.map((row) => (row.id === editing.id ? { ...row, text: editing.text, enabled: editing.enabled } : row))));
    } else {
      setClauses((prev) => normalizeClauseRows(prev.map((row) => (
        row.id === editing.id
          ? { ...row, article_no: Number(editing.article_no) || 1, title: editing.title, content: editing.content, enabled: editing.enabled }
          : row
      ))));
    }
    setEditing(null);
  };

  return (
    <div data-testid="contract-clauses">
      <AdminPageHeader
        title="계약 조항 관리"
        description="신규 계약 생성 시 활성 조항·특약이 snapshot으로 복사됩니다. 이미 만든 계약의 조항은 Master를 바꿔도 변경되지 않습니다."
        actions={(
          <>
            <button type="button" className="admin-row__btn" disabled={saving || loading} onClick={restore}>기본 조항 복원</button>
            <button
              type="button"
              className="bg-white text-black rounded-lg px-3 py-1.5 text-sm"
              onClick={sub === "specials" ? addSpecial : addClause}
            >
              {sub === "specials" ? "+ 특약 추가" : "+ 조항 추가"}
            </button>
          </>
        )}
      />

      <AdminSubTabs tabs={SUBS} value={sub} onChange={setSection} />

      <div className="admin-toolbar">
        <input
          className="admin-toolbar__search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={sub === "specials" ? "특약 내용 검색" : "조 번호 / 제목 / 본문 검색"}
          data-testid="contract-clause-search"
        />
        <span className="admin-count">{source.length}개</span>
      </div>

      {loading ? (
        <div className="text-sm text-white/40">불러오는 중…</div>
      ) : paged.items.length === 0 ? (
        <AdminEmptyState
          message={sub === "specials" ? "등록된 특약이 없습니다." : "등록된 조항이 없습니다."}
          action={(
            <button type="button" className="admin-row__btn" onClick={sub === "specials" ? addSpecial : addClause}>
              {sub === "specials" ? "특약 추가" : "조항 추가"}
            </button>
          )}
        />
      ) : sub === "specials" ? (
        <div className="admin-row-list">
          {paged.items.map((row) => {
            const absIndex = specials.findIndex((r) => r.id === row.id);
            return (
              <div key={row.id} className="admin-row" data-testid={`contract-special-row-${row.id}`}>
                <div className="admin-row__main">
                  <div className="admin-row__title">특약 {absIndex + 1}</div>
                  <div className="admin-row__preview">{previewText(row.text) || "(내용 없음)"}</div>
                  <div className="admin-row__meta">{row.enabled === false ? "비활성" : "활성"}</div>
                </div>
                <div className="admin-row__actions">
                  <button type="button" className="admin-row__btn" onClick={() => setEditing({ kind: "special", ...row })}>수정</button>
                  <button type="button" className="admin-row__btn" onClick={() => move(specials, setSpecials, row.id, -1)}>↑</button>
                  <button type="button" className="admin-row__btn" onClick={() => move(specials, setSpecials, row.id, 1)}>↓</button>
                  <button type="button" className="admin-row__btn" onClick={() => setSpecials(specials.map((r) => r.id === row.id ? { ...r, enabled: r.enabled === false } : r))}>
                    {row.enabled === false ? "활성" : "비활성"}
                  </button>
                  <button type="button" className="admin-row__btn admin-row__btn--danger" onClick={() => { if (window.confirm("이 특약을 삭제할까요?")) setSpecials(specials.filter((r) => r.id !== row.id)); }}>삭제</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="admin-row-list">
          {paged.items.map((row) => (
            <div key={row.id} className="admin-row" data-testid={`contract-clause-row-${row.id}`}>
              <div className="admin-row__main">
                <div className="admin-row__title">제{row.article_no}조 {row.title || ""}</div>
                <div className="admin-row__preview">{previewText(row.content) || "(본문 없음)"}</div>
                <div className="admin-row__meta">{row.enabled === false ? "비활성" : "활성"}</div>
              </div>
              <div className="admin-row__actions">
                <button type="button" className="admin-row__btn" onClick={() => setEditing({ kind: "clause", ...row })}>수정</button>
                <button type="button" className="admin-row__btn" onClick={() => move(clauses, setClauses, row.id, -1)}>↑</button>
                <button type="button" className="admin-row__btn" onClick={() => move(clauses, setClauses, row.id, 1)}>↓</button>
                <button type="button" className="admin-row__btn" onClick={() => setClauses(clauses.map((r) => r.id === row.id ? { ...r, enabled: r.enabled === false } : r))}>
                  {row.enabled === false ? "활성" : "비활성"}
                </button>
                <button type="button" className="admin-row__btn admin-row__btn--danger" onClick={() => { if (window.confirm("이 조항을 삭제할까요?")) setClauses(clauses.filter((r) => r.id !== row.id)); }}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminPagination page={paged.page} pageSize={pageSize} total={source.length} onPageChange={setPage} />

      <div className="admin-sticky-actions">
        <button
          type="button"
          disabled={saving || loading}
          onClick={() => persist(clauses, specials)}
          className="bg-white text-black px-4 py-2 rounded-lg text-sm"
          data-testid="contract-clauses-save"
        >
          {saving ? "저장 중…" : "기본 조항 저장"}
        </button>
      </div>

      <AdminEditDrawer
        open={!!editing}
        title={editing?.kind === "special" ? "특약 수정" : "조항 수정"}
        onClose={() => setEditing(null)}
        footer={(
          <>
            <button type="button" className="admin-row__btn" onClick={() => setEditing(null)}>취소</button>
            <button type="button" className="bg-white text-black rounded-lg px-4 py-2 text-sm" onClick={saveDrawer}>적용</button>
          </>
        )}
      >
        {editing?.kind === "special" ? (
          <AdminField label="특약 본문">
            <textarea rows={8} value={editing.text || ""} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
          </AdminField>
        ) : editing ? (
          <>
            <AdminField label="조 번호">
              <input type="number" min="1" value={editing.article_no} onChange={(e) => setEditing({ ...editing, article_no: e.target.value })} />
            </AdminField>
            <AdminField label="제목">
              <input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </AdminField>
            <AdminField label="본문">
              <textarea rows={10} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
            </AdminField>
          </>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}
